import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import redirectTo from '@99designs/apps/spa/common/Ssr/redirectTo';
import { Source } from '@99designs/feature-auth';
import { useSite } from '@99designs/apps/spa/common/SiteContext';
import { useAppConfig } from '@99designs/apps/spa/common/AppConfigContext';

import fetchPreAuthorisation from './fetchPreAuthorisation';
import { PageType } from './types';
import Container from './Container';
import PageLayout from './common/PageLayout';
import { AuthConfigContextProvider } from './AuthConfigContext';

export interface PageContainerProps {
    initialPage: PageType;
    variant?: 'designer' | 'invite';
}

function PageContainer({ initialPage, variant }: PageContainerProps) {
    const { recaptchaSiteKey } = useAppConfig();
    const site = useSite();
    const history = useHistory();
    const { search } = useLocation();
    const [loading, setLoading] = useState<boolean>(
        () => initialPage === 'login' || initialPage === 'signup'
    );
    const [showRecaptcha, setShowRecaptcha] = useState(true);
    const [source, setSource] = useState(() => {
        const p = new URLSearchParams(search);

        return (p.get('source') as Source) ?? Source.LOGIN_PAGE;
    });
    const [preAuthError, setPreAuthError] = useState<Error | null>(null);
    const [page, setPage] = useState(initialPage);
    const [email, setEmail] = useState('');
    const [{ clientError, recaptchaSkipToken, resetSuccessReason, target }] = useState(() => {
        const p = new URLSearchParams(search);

        const clientError = p.get('error') ?? undefined;
        const recaptchaSkipToken = p.get('recaptcha-skip-token') ?? undefined;
        const resetSuccessReason = p.get('reason') ?? undefined;
        const target = (p.get('target') || p.get('from')) ?? undefined;

        p.delete('error');
        p.delete('reason');

        history.replace({
            search: p.toString(),
        });

        return {
            clientError,
            recaptchaSkipToken,
            resetSuccessReason,
            target,
        };
    });

    useEffect(() => {
        if (page === 'login' || page === 'signup') {
            setLoading(true);

            fetchPreAuthorisation(search)
                .then((data) => {
                    if (data.target) {
                        redirectTo(data.target);
                        return null;
                    } else {
                        if (data.skip_recaptcha) {
                            setShowRecaptcha(false);
                        }
                        if (data.email) {
                            setEmail(data.email);
                        }
                        if (data.source) {
                            setSource(data.source);
                        }
                        if (data.masquerading) {
                            setPage('masquerading');
                        }
                    }

                    setLoading(false);
                })
                .catch((e) => {
                    setLoading(false);
                    setPreAuthError(e);
                });
        }
    }, [page]);

    function handleSetPage(
        e: React.MouseEvent<HTMLAnchorElement> | null,
        newPage: PageType,
        link: string
    ) {
        if (newPage === 'login' || newPage === 'signup') {
            setLoading(true);
        }

        setPage(newPage);

        if (link) {
            history.push(link);
        }
    }

    if (preAuthError) {
        throw preAuthError;
    }

    return (
        <AuthConfigContextProvider value={{ locale: site.siteConfig.locale, recaptchaSiteKey }}>
            <PageLayout>
                <Container
                    email={email}
                    loading={loading}
                    clientError={clientError}
                    onSetPage={handleSetPage}
                    page={page}
                    recaptchaSkipToken={recaptchaSkipToken}
                    resetSuccessReason={resetSuccessReason}
                    source={source}
                    showRecaptcha={showRecaptcha}
                    target={target}
                    variant={variant}
                />
            </PageLayout>
        </AuthConfigContextProvider>
    );
}

export default PageContainer;
